import type { ReactNode } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type AccordionItem = { id: string; title: ReactNode; content: ReactNode; defaultOpen?: boolean };

export type AccordionProps = {
  items: AccordionItem[];
  /** Heading level of each title, so the questions sit in the page outline (FAQ sections). */
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

/**
 * Questions and answers that open in place (FAQ, "how we score"). Native details/summary: works
 * without JavaScript, the answer stays in the HTML for search engines, find-in-page opens it.
 */
export function Accordion({ items, headingLevel = 3, className }: AccordionProps) {
  const Heading = `h${headingLevel}` as 'h3';
  return (
    <div className={cx('divide-y divide-jc-border border-y border-jc-border', className)}>
      {items.map((item) => (
        <details key={item.id} open={item.defaultOpen} className="group">
          <summary
            className={cx(
              'flex cursor-pointer list-none items-center gap-4 py-4 [&::-webkit-details-marker]:hidden',
              'rounded-jc-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
            )}
          >
            <Heading className="min-w-0 flex-1 type-subheading text-jc-ink">{item.title}</Heading>
            <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-jc-elevated text-jc-ink">
              <Icon icon={Plus} size="sm" className="group-open:hidden" />
              <Icon icon={Minus} size="sm" className="hidden group-open:block" />
            </span>
          </summary>
          <div className="pb-5 pr-12 text-sm text-jc-ink-secondary">{item.content}</div>
        </details>
      ))}
    </div>
  );
}
